import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useQuery} from 'react-query';
import {DashboardLayout, SkeletonLoader} from '../../../components';
import {
  baasMobile,
  balanceIcon,
  bank,
  copy,
  green,
  red,
} from 'assets/images/images';
import CurrencyDropdown from './components/CurrencyDropdown';
import OverviewChart from './components/OverviewChart';
import CardSummary from 'components/CardSummary';
import {useAllApps} from '../../../hooks/useApps';
import {
  fetchBAASBreakdown,
  fetchBAASChart,
  fetchBAASCustomerSummary,
} from '../../../requests/queries/baas';
import {Period} from '../Reconciliation/components/Period';
import {verificationChartPeriod} from '../Reconciliation/constants';
import {toast} from 'react-toastify';
import {useBaasSettlement} from './hooks/useBaasSettlement';

export const handleCopy = text => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      toast.success('Copied successfully');
    })
    .catch(err => {
      toast.error('Failed to copy');
    });
};

function Overview() {
  const [createPin, setCreatePin] = useState(false);
  const [date, setDate] = useState('monthly');
  const [selectedCurrency, setSelectedCurrency] = useState({
    code: 'NGN',
    symbol: '₦',
  });
  const queryParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );

  const {userDetails} = useSelector(state => state?.auth);
  const {userApps, isLoading: appsLoading} = useAllApps();
  const {user_account, isSettlementLoading, isFetching} =
    useBaasSettlement(selectedCurrency);

  const appIds = useMemo(
    () => (!appsLoading && userApps.length > 0 ? userApps[0]?.value : ''),
    [appsLoading, userApps],
  );

  const params = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      ...(appIds && {app_id: appIds}),
      currency: selectedCurrency.code,
    }),
    [appIds, userDetails, selectedCurrency],
  );

  const chartsParams = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      period: date,
      currency: selectedCurrency.code,
    }),
    [date, userDetails, selectedCurrency],
  );

  const {data: breakdown, isLoading: isBreakdownLoading} = useQuery(
    ['baas-breakdown', {params}],
    () => fetchBAASBreakdown({params}),
    {
      enabled: !!selectedCurrency,
    },
  );

  const {data: charts, isLoading: isChartsLoading} = useQuery(
    ['baas-charts', {params: {...chartsParams}}],
    () => fetchBAASChart({...chartsParams}),
    {
      meta: {preventGlobalError: true},
      enabled: !!selectedCurrency,
    },
  );

  const {data: customerSummary, isLoading: isCustomerSummaryLoading} = useQuery(
    ['baas-customer-summary', {params}],
    () => fetchBAASCustomerSummary({params}),
    {
      meta: {preventGlobalError: true},
      enabled: !!selectedCurrency,
    },
  );

  const ANALYSIS_DATA = useMemo(
    () => (!isChartsLoading && charts ? charts?.entity : []),
    [charts, isChartsLoading],
  );

  const account_breakdown = useMemo(
    () => !isBreakdownLoading && breakdown && breakdown?.entity,
    [isBreakdownLoading, breakdown],
  );

  const userAccountDetails = useMemo(
    () => ({
      bank_name: user_account?.bank_name,
      account_number: user_account?.account_number,
      account_name: user_account?.account_name,
    }),
    [user_account],
  );

  useEffect(() => {
    if (!isSettlementLoading && !isFetching && user_account) {
      setCreatePin(!user_account?.has_pin);
    }
  }, [isFetching, isSettlementLoading, user_account?.has_pin, user_account]);

  const formatNumber = number => number?.toLocaleString() || '0';

  return (
    <DashboardLayout
      bg="bg-white80"
      topPadding="pt-8"
      xlLeftMargin="xl:ml-12"
      breadCrumbs={
        <div className="flex items-center">
          <img src={baasMobile} alt="Mobile" />
          <p className="ml-2 -mb-1">Overview</p>
        </div>
      }
    >
      <div className="flex md:items-center justify-between flex-col md:flex-row gap-6 lg:gap-0">
        <div>
          <h1 className="font-medium text-[20px] text-grey">Overview</h1>
        </div>
      </div>

      <CurrencyDropdown onCurrencyChange={setSelectedCurrency} />

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="flex flex-col gap-3">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2">
              <img alt="wallet" src={balanceIcon} />

              <div className="flex flex-col gap-1">
                <p className="text-body  font-medium text-sm">
                  Available Balance
                </p>
                {isSettlementLoading ? (
                  <SkeletonLoader
                    style={{
                      width: 150,
                      height: 25,
                      marginBottom: 10,
                    }}
                  />
                ) : (
                  <>
                    <h2 className="font-semibold text-[24px] text-grey">
                      {selectedCurrency.symbol}
                      {formatNumber(user_account?.amount || 0)}
                    </h2>
                    <h4 className="mt-1 text-[10px] font-medium text-deepBlue300">
                      Ledger Balance:{' '}
                      <span className="ml-2 font-semibold text-[14px]">
                        {selectedCurrency.symbol}
                        {formatNumber(user_account?.ledger_balance || 0)}
                      </span>
                    </h4>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-4 h-full">
            <CardSummary
              img={green}
              title="Available Customers"
              body={formatNumber(customerSummary?.entity?.total)}
              titleClassName="text-sm"
              bodyClassName="text-[24px]"
              loading={isCustomerSummaryLoading}
            />
            <CardSummary
              img={red}
              title="Estimated Settlement"
              body={`${selectedCurrency.symbol}${formatNumber(
                account_breakdown?.total_settlement || 0,
              )}`}
              titleClassName="text-sm"
              bodyClassName="text-[24px]"
              loading={isBreakdownLoading}
            />
          </div>
        </div>
        <div className="bg-white rounded-lg p-8">
          <div className="flex items-center justify-between pb-3 border border-transparent border-b-[#E1E4EA]">
            <div className="flex items-center gap-2">
              <img src={bank} alt="bank" />
              <p className="font-medium text-black text-base">
                Account Details
              </p>
            </div>
            <button
              onClick={() =>
                handleCopy(
                  `Bank: ${userAccountDetails.bank_name}, Account Name: ${userAccountDetails.account_name}, Account Number: ${userAccountDetails.account_number}`,
                )
              }
              className="px-4 py-[6px] rounded-full border border-[#F2F4F7] text-sm text-black flex items-center gap-1  "
            >
              <img src={copy} alt="copy" />
              Copy
            </button>
          </div>
          <div className="mt-4 flex flex-col gap-8">
            {Object.entries(userAccountDetails || {}).map(([key, value]) => (
              <div
                key={key}
                className="flex items-center justify-between text-sm"
              >
                <p className="text-[#868C98] capitalize">
                  {key.replaceAll('_', ' ')}
                </p>
                {isSettlementLoading || appsLoading ? (
                  <SkeletonLoader
                    style={{
                      width: 150,
                      height: 18,
                      marginBottom: 10,
                    }}
                  />
                ) : (
                  <p className="text-black font-medium flex items-center   gap-2">
                    {value || 'N/A'}
                    {value && key === 'account_number' && (
                      <span
                        onClick={() => handleCopy(value)}
                        className="cursor-pointer"
                      >
                        <img src={copy} alt="copy" />
                      </span>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white mt-10 rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-0 justify-between">
          <div className="flex items-center gap-6">
            <h1 className="text-deepBlue font-semibold text-[24px]">
              Transactions
            </h1>
            <div className="flex items-center gap-4 mb-1  ">
              <div className="text-[10px] font-medium text-body   uppercase flex items-center gap-1">
                <div className="min-w-[16px] min-h-[16px] rounded bg-[#71B167]" />
                inflow
              </div>

              <div className="text-[10px] font-medium text-body    uppercase flex items-center gap-1">
                <div className="min-w-[16px] min-h-[16px] rounded bg-[#C0483F]" />
                outflow
              </div>
            </div>
          </div>
          <Period
            date={date}
            setDate={setDate}
            datePeriod={queryParams.get('period')}
            otherChartPeriod={verificationChartPeriod}
          />{' '}
        </div>
        <div className="mt-7 grid lg:grid-cols-4 gap-3">
          <CardSummary
            img={green}
            title="Total Collections"
            body={`${selectedCurrency.symbol}${formatNumber(
              account_breakdown?.total_deposit || 0,
            )}`}
            titleClassName="!text-[10px]"
            bodyClassName="text-[20px]"
            className="bg-white80"
            loading={isBreakdownLoading}
          />

          <CardSummary
            img={red}
            title="Total Payout"
            body={`${selectedCurrency.symbol}${formatNumber(
              account_breakdown?.total_transfer || 0,
            )}`}
            titleClassName="!text-[10px]"
            bodyClassName="text-[20px]"
            className="bg-white80"
            loading={isBreakdownLoading}
          />
        </div>

        <OverviewChart
          data={ANALYSIS_DATA}
          periodType={date}
          loading={isChartsLoading}
        />
      </div>
    </DashboardLayout>
  );
}

export default Overview;
